import { DeviceResponseReportWrapper } from "src/repositories/api/dto/DeviceResponseReportWrapper";
import {
  TimeWindowGroupCreateInDto,
  TimeWindowGroupPatchInDto,
} from "src/repositories/api/time-window-group/dto/Dto";
import { timeWindowGroupsApi } from "src/repositories/api/time-window-group/TimeWindowGroups.api";
import { GeneralApiResponse } from "src/repositories/api/Utils/ResponseUtility";
import {
  createFailedResponse,
  createSuccessResponse,
  FailedResponse,
} from "src/repositories/model/FailableResponse";
import groupService from "src/services/core/group/Group.service";
import { reduxStore } from "src/services/redux/ReduxStore";
import timeWindowGroupListSlice from "src/services/redux/slices/TimeWindowGroupSlice";
import {
  handleGeneralErrorPostAction,
  handleGeneralErrorResponse,
  methodWrapper,
} from "src/services/utils/Utils.service";

async function get(
  groupId: number,
  top?: number,
  skip?: number,
  nameFilter?: string,
  checkCache: boolean = true,
  saveToCache: boolean = true
): Promise<
  GeneralApiResponse<TimeWindowGroup[]> | FailedResponse<"no-selected-groups">
> {
  if (checkCache) {
    const cacheResult = reduxStore.getState().timeWindowGroupSlice;
    if (cacheResult != null) {
      return createSuccessResponse(cacheResult);
    }
  }

  const result = await timeWindowGroupsApi.get(groupId, top, skip, nameFilter);
  if (!result.success) {
    handleGeneralErrorResponse(result);
    return result;
  }

  result.value = result.value.sort((a, b) => a.idOnDevice - b.idOnDevice);
  if (saveToCache) {
    reduxStore.dispatch(timeWindowGroupListSlice.actions.update(result.value));
  }
  return result;
}

async function getCount(
  groupId: number,
  nameFilter?: string
): Promise<GeneralApiResponse<number> | FailedResponse<"no-selected-groups">> {
  const groupResult = await groupService.getSelectedGroup();
  if (!groupResult.success) {
    return groupResult;
  }
  if (groupResult.value == null) {
    return createFailedResponse("no-selected-groups");
  }

  const result = await timeWindowGroupsApi.getCount(
    groupResult.value.id,
    nameFilter
  );
  if (!result.success) {
    handleGeneralErrorResponse(result);
  }
  return result;
}

async function delete_(
  id: number,
  deviceSns?: string[],
  updateCache: boolean = true
): Promise<GeneralApiResponse<DeviceResponseReportWrapper>> {
  const result = await timeWindowGroupsApi.delete_(id, deviceSns);
  if (!result.success) {
    handleGeneralErrorResponse(result);
  } else if (updateCache) {
    reduxStore.dispatch(timeWindowGroupListSlice.actions.remove([id]));
  }
  return result;
}

async function create(
  dto: TimeWindowGroupCreateInDto,
  deviceSns?: string[],
  updateCache: boolean = true
): Promise<
  | GeneralApiResponse<DeviceResponseReportWrapper<TimeWindowGroup>>
  | FailedResponse<"idOnDevice-conflict", number>
> {
  const result = await timeWindowGroupsApi.create(dto, deviceSns);
  if (!result.success) {
    handleGeneralErrorResponse(result);
  } else if (updateCache) {
    reduxStore.dispatch(
      timeWindowGroupListSlice.actions.add([result.value.value])
    );
  }
  return result;
}

async function patch(
  id: number,
  dto: TimeWindowGroupPatchInDto,
  deviceSns?: string[],
  updateCache: boolean = true
): Promise<
  | GeneralApiResponse<DeviceResponseReportWrapper>
  | FailedResponse<"idOnDevice-conflict", number>
> {
  const result = await timeWindowGroupsApi.patch(id, dto, deviceSns);
  if (!result.success) {
    handleGeneralErrorResponse(result);
  } else if (updateCache) {
    reduxStore.dispatch(
      timeWindowGroupListSlice.actions.patchItem({ id, ...dto })
    );
  }

  return result;
}

export const timeWindowGroupsService = {
  get,
  getCount: methodWrapper(
    timeWindowGroupsApi.getCount,
    handleGeneralErrorPostAction
  ),
  create,
  patch,
  delete_,
};
